import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Course, Enrollment } from "@/types/courseTypes";
import { getAllCourses } from "@/utils/firestoreService";
import { useToast } from "@/hooks/use-toast";
import SearchBar from "@/components/courses/SearchBar";
import CourseList from "@/components/courses/CourseList";

const CourseCatalog = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);

  // Mock student ID
  const studentId = "student123";

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      try {
        const fetchedCourses = await getAllCourses();
        // Filter to show both practical and individual courses
        const practicalAndIndividualCourses = fetchedCourses.filter(course => 
          (course.type === "practical" && course.subtype === "practical") || 
          (course.type === "practical" && course.subtype === "individual")
        );
        setCourses(practicalAndIndividualCourses);
        
        const storedEnrollments = localStorage.getItem("enrollments");
        if (storedEnrollments) {
          const parsedEnrollments = JSON.parse(storedEnrollments);
          setEnrollments(parsedEnrollments.filter((e: Enrollment) => e.studentId === studentId));
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
        toast({
          title: "Error",
          description: "Failed to load courses. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [toast, studentId]);

  const filteredCourses = courses.filter(
    (course) =>
      course.published &&
      (course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
       course.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const isEnrolled = (courseId: string) => {
    return enrollments.some(
      (enrollment) => 
        enrollment.courseId === courseId && 
        enrollment.paymentStatus === "paid"
    );
  };

  const viewCourse = (courseId: string) => {
    navigate(`/student-dashboard/practical-course/${courseId}`);
  };

  return (
    <DashboardLayout role="student">
      <div className="mb-8 space-y-4">
        <h1 className="text-3xl font-bold text-gray-900">Available Driving Courses</h1>
        <p className="text-gray-600">
          Browse and enroll in our practical driving courses and individual lessons
        </p>
      </div>

      <div className="space-y-6">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          courseType="practical"
        />

        <CourseList
          courses={filteredCourses}
          isLoading={loading}
          courseType="practical"
          isEnrolled={isEnrolled}
          onViewCourse={viewCourse}
          enrollments={enrollments}
        />
      </div>
    </DashboardLayout>
  );
};

export default CourseCatalog;
